import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { fetchPromotions, savePromotions } from '../services/api.ts';
import { Promotion } from '../types';
import Layout from '../components/layout/Layout.tsx';

const daysOfWeek = [
  { id: 'monday', label: 'Segunda-feira' },
  { id: 'tuesday', label: 'Terça-feira' },
  { id: 'wednesday', label: 'Quarta-feira' },
  { id: 'thursday', label: 'Quinta-feira' },
  { id: 'friday', label: 'Sexta-feira' },
  { id: 'saturday', label: 'Sábado' },
  { id: 'sunday', label: 'Domingo' },
];

const Promotions = () => {
  const [promotions, setPromotions] = useState<Record<string, Promotion>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const data = await fetchPromotions();
        setPromotions(data);
      } catch (error) {
        console.error('Error fetching promotions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (day: string, field: 'attractions' | 'promotions', value: string) => {
    setPromotions({
      ...promotions,
      [day]: {
        ...promotions[day],
        [field]: value,
      },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      await savePromotions(promotions);
      setSaveSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving promotions:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
  <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
</div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">Atrações e Promoções do Dia</h2>
            <p className="text-sm text-gray-500 mt-1">
              Configure as informações que a IA irá fornecer aos clientes para cada dia da semana.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              {daysOfWeek.map((day) => (
                <div key={day.id} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-md font-medium text-gray-700 mb-3">{day.label}</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor={`${day.id}-attractions`} className="block text-sm font-medium text-gray-700 mb-1">
                        Atrações
                      </label>
                      <textarea
                        id={`${day.id}-attractions`}
                        rows={2}
                        value={promotions[day.id]?.attractions || ''}
                        onChange={(e) => handleInputChange(day.id, 'attractions', e.target.value)}
                        placeholder="Ex: Música ao vivo, DJ Convidado..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor={`${day.id}-promotions`} className="block text-sm font-medium text-gray-700 mb-1">
                        Promoções
                      </label>
                      <textarea
                        id={`${day.id}-promotions`}
                        rows={2}
                        value={promotions[day.id]?.promotions || ''}
                        onChange={(e) => handleInputChange(day.id, 'promotions', e.target.value)}
                        placeholder="Ex: 2x1 em drinks, 20% de desconto em pratos..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div>
                {saveSuccess && (
                  <p className="text-sm text-green-600 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    Informações salvas com sucesso!
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={isSaving}
                className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2f7d72] hover:bg-[#27685f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
              >
                {isSaving ? (
                  <div className="w-5 h-5 rounded-full animate-spin border-2 border-solid border-white border-t-transparent"></div>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Salvar e Enviar Atualização para IA
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Promotions;
